import React, { useCallback, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import omit from 'lodash/omit';
import Tree from '../../../lib/tree';
import { getConfig } from '../../../lib/configure';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import { defaultAggregationRenderer } from './Column';
import TableContext from './TableContext';
import { getColumnKey, getColumnLock, getHeader, isStickySupport } from './utils';
import { ColumnLock } from './enum';
import TableCellInner from './TableCellInner';
import AggregationButton from './AggregationButton';
const TableCell = observer(function TableCell(props) {
    const { columnGroup, record, isDragging, provided, colSpan, className, children, disabled, inView } = props;
    const { column, key } = columnGroup;
    const { tableStore, prefixCls, dataSet, expandIconAsCell, aggregation: tableAggregation, rowHeight } = useContext(TableContext);
    const cellPrefix = `${prefixCls}-cell`;
    const tableColumnOnCell = getConfig('tableColumnOnCell');
    const getInnerNode = useCallback((col, onCellStyle, inAggregation) => (React.createElement(TableCellInner, { column: col, record: record, style: onCellStyle, disabled: disabled, inAggregation: inAggregation, prefixCls: cellPrefix, colSpan: colSpan }, children)), [record, disabled, children, cellPrefix, colSpan]);
    const getColumnsInnerNode = useCallback((columns) => {
        return columns.map((col) => {
            const { hidden, hiddenInAggregation } = col;
            if (!hidden && !(typeof hiddenInAggregation === 'function' ? hiddenInAggregation(record) : hiddenInAggregation)) {
                const { onCell } = col;
                const isBuiltInColumn = tableStore.isBuiltInColumn(col);
                const columnOnCell = !isBuiltInColumn && (onCell || tableColumnOnCell);
                const cellExternalProps = typeof columnOnCell === 'function'
                    ? columnOnCell({
                        dataSet,
                        record,
                        column: col,
                    })
                    : {};
                const columnKey = getColumnKey(col);
                const header = getHeader(col, dataSet, tableAggregation);
                // 只有全局属性时候的样式可以继承给下级满足对td的样式能够一致表现
                const onCellStyle = !isBuiltInColumn && tableColumnOnCell === columnOnCell && typeof tableColumnOnCell === 'function' ? omit(cellExternalProps.style, ['width', 'height']) : undefined;
                const childColumns = col.children;
                if (childColumns && childColumns.length) {
                    return (React.createElement(Tree.TreeNode, Object.assign({}, cellExternalProps, { key: columnKey, title: header }), getColumnsInnerNode(childColumns)));
                }
                const innerNode = getInnerNode(col, onCellStyle, true);
                return (React.createElement(Tree.TreeNode, Object.assign({}, cellExternalProps, { key: columnKey, title: React.createElement(React.Fragment, null,
                        React.createElement("span", { className: `${cellPrefix}-label` }, header),
                        innerNode) })));
            }
            return undefined;
        });
    }, [tableStore, record, dataSet, cellPrefix, getInnerNode, tableColumnOnCell, tableAggregation]);
    const renderInnerNode = (aggregation, onCellStyle) => {
        if (expandIconAsCell && children) {
            return (React.createElement("span", { className: classNames(`${cellPrefix}-inner`, { [`${cellPrefix}-inner-row-height-fixed`]: rowHeight !== 'auto' }), style: { textAlign: 'center', ...onCellStyle } }, children));
        }
        if (aggregation) {
            const { column: { children: childColumns } } = columnGroup;
            if (childColumns) {
                const visibleChildren = childColumns.filter(child => !child.hidden);
                const { length } = visibleChildren;
                if (length > 0) {
                    const { renderer = defaultAggregationRenderer, aggregationLimit, aggregationDefaultExpandedKeys, aggregationDefaultExpandAll } = column;
                    const expanded = tableStore.isAggregationCellExpanded(record, key);
                    const hasExpand = length > aggregationLimit;
                    const nodes = hasExpand && !expanded ? visibleChildren.slice(0, aggregationLimit - 1) : visibleChildren;
                    const text = (React.createElement(Tree, { prefixCls: `${cellPrefix}-tree`, virtual: false, focusable: false, defaultExpandedKeys: aggregationDefaultExpandedKeys, defaultExpandAll: aggregationDefaultExpandAll },
                        getColumnsInnerNode(nodes),
                        hasExpand && (React.createElement(Tree.TreeNode, { title: React.createElement(AggregationButton, { expanded: expanded, record: record, columnGroup: columnGroup }) }))));
                    return (React.createElement("div", { className: `${cellPrefix}-inner` }, renderer({ text, record, dataSet, aggregation: tableAggregation })));
                }
            }
        }
        return getInnerNode(column, onCellStyle);
    };
    const { style, lock } = column;
    const columnLock = isStickySupport() && getColumnLock(lock);
    const baseStyle = (() => {
        if (columnLock) {
            if (columnLock === ColumnLock.left) {
                return {
                    ...style,
                    left: pxToRem(columnGroup.left),
                };
            }
            if (columnLock === ColumnLock.right) {
                return {
                    ...style,
                    right: pxToRem(colSpan && colSpan > 1 ? 0 : columnGroup.right),
                };
            }
        }
        return style;
    })();
    const baseClassName = classNames(cellPrefix, {
        [`${cellPrefix}-fix-${columnLock}`]: columnLock,
    });
    if (inView === false || columnGroup.inView === false) {
        return (React.createElement("td", { colSpan: colSpan, "data-index": key, className: baseClassName, style: baseStyle }));
    }
    const { onCell, aggregation } = column;
    const isBuiltInColumn = tableStore.isBuiltInColumn(column);
    const columnOnCell = !isBuiltInColumn && (onCell || tableColumnOnCell);
    const cellExternalProps = typeof columnOnCell === 'function'
        ? columnOnCell({
            dataSet: record.dataSet,
            record,
            column,
        })
        : {};
    const cellStyle = {
        ...baseStyle,
        ...cellExternalProps.style,
        ...(provided && { cursor: 'move' }),
    };
    const classString = classNames(baseClassName, {
        [`${cellPrefix}-aggregation`]: aggregation,
    }, column.className, className, cellExternalProps.className);
    const widthDraggingStyle = () => {
        const draggingStyle = {};
        if (isDragging) {
            const dom = tableStore.node.element.querySelector(`.${prefixCls}-tbody .${prefixCls}-cell[data-index="${key}"]`);
            if (dom) {
                draggingStyle.width = dom.clientWidth;
                draggingStyle.whiteSpace = 'nowrap';
            }
        }
        return draggingStyle;
    };
    // 只有全局属性时候的样式可以继承给下级满足对td的样式能够一致表现
    const onCellStyle = !isBuiltInColumn && tableColumnOnCell === columnOnCell && typeof tableColumnOnCell === 'function' ? omit(cellExternalProps.style, ['width', 'height']) : undefined;
    return (React.createElement("td", Object.assign({ colSpan: colSpan }, cellExternalProps, { className: classString, "data-index": key }, (provided && provided.dragHandleProps), { style: { ...omit(cellStyle, ['width', 'height']), ...widthDraggingStyle() } }), renderInnerNode(aggregation, onCellStyle)));
});
TableCell.displayName = 'TableCell';
export default TableCell;
